import { FormField, FormFieldRow, ReadOnlyField, TextArea, TextInput } from '@msaf/core-react'
import { useContext } from 'react'
import { TOOLTIP } from '../../../../constants/tooltips'
import { AquiferTestObservationWellFieldsEdit, FieldTypes } from '../../../../types/form-types'
import { ValidationErrors } from '../../../hooks/use-edit-form'
import { getLookupData } from '../../../services/api'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { UserContext } from '../../../state/user-state'
import LookupSearch from '../../wells/lookup-search'

export interface AquiferTestObservationWellsEditFormInterface {
  fields: Partial<AquiferTestObservationWellFieldsEdit>
  wellId: string
  isLoading: boolean
  setValue: (key: keyof AquiferTestObservationWellFieldsEdit, value: FieldTypes) => void
  index?: number
  validationErrors?: ValidationErrors<AquiferTestObservationWellFieldsEdit>
}

export default function AquiferTestObservationWellsEditForm({
  fields,
  wellId,
  isLoading,
  setValue,
  index,
  validationErrors = {},
}: AquiferTestObservationWellsEditFormInterface) {
  const { state } = useContext(UserContext)
  return (
    <>
      <FormField
        isSkeleton={isLoading}
        label='Observation well'
        labelId={`observationWellLabel${index ? index + 1 : ''}`}
        validationMessages={validationErrors?.observationWell}
      >
        <LookupSearch
          isSkeleton={isLoading}
          searchFunction={(wellName: string) =>
            getLookupData('observationWell', state.token || '', state.auth_method, { wellName, wellId })
          }
          fieldId='observationWell'
          labelledBy={`observationWellLabel${index ? index + 1 : ''}`}
          value={`${fields.observationWell ?? ''}`}
          setValue={({ value }) => setValue('observationWell', value)}
          isInvalid={!!validationErrors?.observationWell}
        />
      </FormField>

      <FormFieldRow>
        <FormField
          isSkeleton={isLoading}
          label='Distance to pumped well (m)'
          htmlFor={`distanceToPumpedWell${index ? index + 1 : ''}`}
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`distanceToPumpedWellLabel${index}`}
            value={`${fields.distanceToPumpedWell ?? ''}`}
          />
        </FormField>

        <FormField
          toolTipMessage={TOOLTIP.maxDrawdown}
          isSkeleton={isLoading}
          label='Max drawdown (m)'
          htmlFor={`maxDrawdown${index ? index + 1 : ''}`}
          validationMessages={validationErrors?.maxDrawdown}
        >
          <TextInput
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            id={`maxDrawdown${index ? index + 1 : ''}`}
            value={`${fields.maxDrawdown ?? ''}`}
            setValue={(value) => setValue('maxDrawdown', value)}
            isInvalid={!!validationErrors?.maxDrawdown}
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField
          isSkeleton={isLoading}
          label='Transmissivity (m2/d)'
          htmlFor={`transmissivity${index ? index + 1 : ''}`}
          toolTipMessage={TOOLTIP.transmissivity}
          validationMessages={validationErrors?.transmissivity}
        >
          <TextInput
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            id={`transmissivity${index ? index + 1 : ''}`}
            value={`${fields.transmissivity ?? ''}`}
            setValue={(value) => setValue('transmissivity', value)}
            isInvalid={!!validationErrors?.transmissivity}
          />
        </FormField>

        <FormField
          isSkeleton={isLoading}
          label='Overlying aquifer - transmissivity (m2/d)  '
          htmlFor={`transmissivityOverlyingLayer${index ? index + 1 : ''}`}
          validationMessages={validationErrors?.transmissivityOverlyingLayer}
        >
          <TextInput
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            id={`transmissivityOverlyingLayer${index ? index + 1 : ''}`}
            value={`${fields.transmissivityOverlyingLayer ?? ''}`}
            setValue={(value) => setValue('transmissivityOverlyingLayer', value)}
            isInvalid={!!validationErrors?.transmissivityOverlyingLayer}
          />
        </FormField>
      </FormFieldRow>
      <FormFieldRow>
        <FormField
          toolTipMessage={TOOLTIP.storativity}
          isSkeleton={isLoading}
          label='Storativity'
          htmlFor={`storativity${index ? index + 1 : ''}`}
          validationMessages={validationErrors?.storativity}
        >
          <TextInput
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            id={`storativity${index ? index + 1 : ''}`}
            value={`${fields.storativity ?? ''}`}
            setValue={(value) => setValue('storativity', value)}
            isInvalid={!!validationErrors?.storativity}
          />
        </FormField>

        <FormField
          isSkeleton={isLoading}
          label='Overlying aquifer - storativity'
          htmlFor={`storativityOverlyingLayer${index ? index + 1 : ''}`}
          validationMessages={validationErrors?.storativityOverlyingLayer}
        >
          <TextInput
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            id={`storativityOverlyingLayer${index ? index + 1 : ''}`}
            value={`${fields.storativityOverlyingLayer ?? ''}`}
            setValue={(value) => setValue('storativityOverlyingLayer', value)}
            isInvalid={!!validationErrors?.storativityOverlyingLayer}
          />
        </FormField>
      </FormFieldRow>

      <FormField
        toolTipMessage={TOOLTIP.leakage}
        isSkeleton={isLoading}
        label='Leakage (m)'
        htmlFor={`leakage${index ? index + 1 : ''}`}
        validationMessages={validationErrors?.leakage}
      >
        <TextInput
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          id={`leakage${index ? index + 1 : ''}`}
          value={`${fields.leakage ?? ''}`}
          setValue={(value) => setValue('leakage', value)}
          isInvalid={!!validationErrors?.leakage}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.kB}
        isSkeleton={isLoading}
        label='K’/B’'
        htmlFor={`kB${index ? index + 1 : ''}`}
        validationMessages={validationErrors?.kB}
      >
        <TextInput
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          id={`kB${index ? index + 1 : ''}`}
          value={`${fields.kB ?? ''}`}
          setValue={(value) => setValue('kB', value)}
          isInvalid={!!validationErrors?.kB}
        />
      </FormField>

      <FormField isSkeleton={isLoading} label='Comment' htmlFor={`comment${index ? index + 1 : ''}`}>
        <TextArea
          isSkeleton={isLoading}
          id={`comment${index ? index + 1 : ''}`}
          value={`${fields.comment ?? ''}`}
          onChange={(e) => setValue('comment', e.target.value)}
        />
      </FormField>
    </>
  )
}
